import actionTypes from 'constants/actionTypes'
import defaultsDeep from 'lodash/defaultsDeep'
import cloneDeep from 'lodash/cloneDeep'
import each from 'lodash/each'
import reduce from 'lodash/reduce'

type NodeEdgesMap = Record<string, Record<string, string>>

interface EdgeEntity {
  id?: string
  from?: string
  to?: string
}

interface ByNodeAction {
  type?: string
  payload?: {
    id?: string
    ids?: string[]
    from?: string
    to?: string
    data?: {
      id?: string
      ids?: string[]
      from?: string
      to?: string
      edges?: EdgeEntity[]
    }
  }
  previousPayload?: {
    id?: string
    ids?: string[]
  }
}


// state is a {<nodeId>: {<edgeId>: <edgeId>}}
const byNode = (state: NodeEdgesMap = {}, action: ByNodeAction): NodeEdgesMap => {
  const actionType = action.type ?? ''
  switch (true) {
    case actionType === actionTypes.EDGES.ONE.CREATE.REQUEST: {
      const id = action.payload?.id
      const toId = action.payload?.to
      const fromId = action.payload?.from
      if (!id || !toId || !fromId) return state
      return defaultsDeep(
        {
          [toId]: { [id]: id }
        },
        {
          [fromId]: { [id]: id }
        },
        state
      )
    }
    case [
      actionTypes.EDGES.ONE.CREATE.RECEIVE,
      actionTypes.EDGES.ONE.CREATE.NOTIFY
    ].includes(actionType): {
      const id = action.payload?.data?.id
      const toId = action.payload?.data?.to
      const fromId = action.payload?.data?.from
      if (!id || !toId || !fromId) return state
      const res = defaultsDeep(
        {
          [toId]: { [id]: id }
        },
        {
          [fromId]: { [id]: id }
        },
        state
      )
      return res
    }
    case actionType === actionTypes.EDGES.ONE.DELETE.REQUEST: {
      const id = action.payload?.id
      if (!id) return state
      const res = cloneDeep(state)
      each(res, (n: Record<string, string>) => {
        delete n[id]
      })
      return res
    }
    case [
      actionTypes.EDGES.ONE.DELETE.RECEIVE,
      actionTypes.EDGES.ONE.DELETE.NOTIFY
    ].includes(actionType): {
      const id = action.payload?.data?.id || action.previousPayload?.id
      if (!id) return state
      const res = cloneDeep(state)
      each(res, (n: Record<string, string>) => {
        delete n[id]
      })
      return res
    }
    case [
      actionTypes.NODES.ONE.DELETE.REQUEST,
      actionTypes.NODES.ONE.DELETE.RECEIVE,
      actionTypes.NODES.ONE.DELETE.NOTIFY
    ].includes(actionType): {
      const id = action.payload?.id || action.payload?.data?.id || action.previousPayload?.id
      if (!id) return state
      const res = cloneDeep(state)
      delete res[id]
      return res
    }
    case [
      actionTypes.EDGES.MULTI.DELETE.REQUEST,
      actionTypes.EDGES.MULTI.DELETE.RECEIVE
    ].includes(actionType): {
      const ids = action.payload?.ids || action.payload?.data?.ids || action.previousPayload?.ids
      const res = cloneDeep(state)
      each(res, (n: Record<string, string>) => {
        each(ids ?? [], (id: string) => {
          delete n[id]
        })
      })
      return res
    }
    case actionType === actionTypes.GRAPH.GET_NEIGHBORHOOD.RECEIVE:
      return reduce(action.payload?.data?.edges ?? [], (es: NodeEdgesMap, edge: EdgeEntity) => {
        if (!edge.id || !edge.to || !edge.from) return es
        return defaultsDeep(
          {
            [edge.to]: { [edge.id]: edge.id }
          },
          {
            [edge.from]: { [edge.id]: edge.id }
          },
          es
        )
      }, state)
    default:
      return state
  }
}

export default byNode
